// First-visit intro screen — shown BEFORE the wizard on a brand-new
// install. Lets the user read "what is Karta Pamet" in their likely
// language (auto-detected from IP) and override the locale before
// committing to the longer wizard questions. Once dismissed, the
// wizard starts.

import { LANG_NATIVE, LANG_TO_LOCALE, LANGS } from "../config/langs";
import { STORAGE_KEYS } from "../config/storage-keys";
import { t } from "../i18n";
import { byId, hide, show } from "./dom";

export interface IntroScreenDeps {
  applyLocale: () => void;
  /** Called once the user clicks "Continuer" — bootstrap hands off to
   *  the wizard from there. */
  onContinue: () => void;
}

/** Render the intro screen with localized copy + locale select seeded
 *  to the currently-saved/detected locale. */
function renderIntroScreen(): void {
  const title = byId("introScreenTitle");
  const desc = byId("introScreenDesc");
  const label = byId("introScreenLocaleLabel");
  const continueBtn = byId("introScreenContinue");
  const select = byId<HTMLSelectElement>("introLocaleSelect");
  if (title) title.textContent = t("tour_intro_title") as string;
  if (desc) desc.textContent = t("intro_screen_desc") as string;
  if (label) label.textContent = t("wizard_locale_label") as string;
  if (continueBtn) continueBtn.textContent = t("wizard_continue") as string;
  if (select) {
    // Native-name labels so the user recognises their language even
    // when the current UI locale is wrong.
    const current = localStorage.getItem(STORAGE_KEYS.LOCALE) || "";
    select.innerHTML = LANGS.map((lang) => {
      const code = LANG_TO_LOCALE[lang];
      const sel = current === code ? " selected" : "";
      return `<option value="${code}"${sel}>${LANG_NATIVE[lang]}</option>`;
    }).join("");
  }
}

export function showIntroScreen(deps: IntroScreenDeps): void {
  const screen = byId("introScreen");
  const select = byId<HTMLSelectElement>("introLocaleSelect");
  const continueBtn = byId<HTMLButtonElement>("introScreenContinue");
  if (!screen || !continueBtn) return;
  renderIntroScreen();
  // Locale change is immediate: persist + re-render all i18n so the
  // user sees the intro copy flip to the newly-picked language.
  if (select) {
    select.onchange = () => {
      localStorage.setItem(STORAGE_KEYS.LOCALE, select.value);
      deps.applyLocale();
      renderIntroScreen();
    };
  }
  continueBtn.onclick = () => {
    // Lock in the selected locale (the select's change handler already
    // persisted it, but this catches the case where the user clicked
    // continue without touching the select — `deps.applyLocale` is a
    // no-op when the locale hasn't changed).
    if (select && select.value) {
      localStorage.setItem(STORAGE_KEYS.LOCALE, select.value);
      deps.applyLocale();
    }
    hide(screen);
    deps.onContinue();
  };
  show(screen);
}
